// dataPoster.js - APIM data POST layer for the RTLS Dashboard frontend
// ─────────────────────────────────────────────────────────────────────────────
// Companion to dataReceiver.js (GET / read-only). Same conventions  base URL
// resolution, optional subscription key, a typed error class, one low-level
// fetch helper that every route function calls through, and per-route JSDoc
// describing exactly what shape goes in and comes back out. This file is
// reserved for everything that WRITES via the dataPost Azure Function;
// dataReceiver.js never mutates data.
//
// ── Route parameter reference ─────────────────────────────────────────────────
// Every call maps to:
//   POST <APIM_BASE_URL>/dataPost?route=<routeName>
//   Body: JSON (see each function's @param for the exact shape)
//
// Implemented routes
//   update-goals    fetchUpdateGoals(payload, signal)

// ─── Base URL ─────────────────────────────────────────────────────────────────
//
// Intentionally the SAME env var as dataReceiver.js  dataServe and dataPost
// are two routes on the same APIM/Function App resource, not two hosts.

const APIM_BASE_URL = (
  window.ENV.APIM_BASE_URL || "http://localhost:7071/api"
).replace(/\/+$/, ""); // strip trailing slash

// ─── APIM subscription key (optional) ────────────────────────────────────────

const APIM_SUBSCRIPTION_KEY = window.ENV.APIM_SUBSCRIPTION_KEY ?? "";

// ─── Internal fetch helper ────────────────────────────────────────────────────

/**
 * Perform a POST request against the dataPost Azure Function.
 *
 * @param {string}      routeName  - Value of the "route" query param
 * @param {unknown}     body       - JSON-serialisable request body
 * @param {AbortSignal} [signal]   - Optional AbortController signal for cancellation
 * @returns {Promise<unknown>}     - Parsed JSON response body
 * @throws {DataPosterError}       - On HTTP error, validation failure, or non-JSON response
 */
async function apimPost(routeName, body, signal) {
  const params = new URLSearchParams({ route: routeName });
  const url = `${APIM_BASE_URL}/dataPost?${params.toString()}`;

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (APIM_SUBSCRIPTION_KEY) {
    headers["Ocp-Apim-Subscription-Key"] = APIM_SUBSCRIPTION_KEY;
  }

  let res;
  try {
    res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal,
    });
  } catch (networkErr) {
    // AbortError is a normal cancellation - re-throw as-is so callers can
    // detect it with err.name === 'AbortError'
    if (networkErr.name === "AbortError") throw networkErr;
    throw new DataPosterError(
      `Network error reaching APIM: ${networkErr.message}`,
      0,
      null,
    );
  }

  // ── Content-type guard ────────────────────────────────────────────────────
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    const preview = await res.text().then((t) => t.slice(0, 300));
    throw new DataPosterError(
      `APIM returned non-JSON for route "${routeName}" ` +
        `(HTTP ${res.status} - ${contentType}). ` +
        `Check REACT_APP_APIM_BASE_URL is correct. Preview: ${preview}`,
      res.status,
      null,
    );
  }

  const json = await res.json();

  if (!res.ok) {
    // dataPost.js serializes errors as { error, message, route } and, for
    // validation_failed specifically, also { validationErrors: [...] }.
    const message =
      json?.message ??
      json?.error ??
      `APIM route "${routeName}" returned HTTP ${res.status}`;
    throw new DataPosterError(message, res.status, json);
  }

  return json;
}

// ─── DataPosterError ──────────────────────────────────────────────────────────

/**
 * Typed error thrown by all dataPoster functions.
 * Callers can inspect .status and .payload for structured error handling.
 *
 * For a validation_failed response (HTTP 400 from dataPost.js's
 * handleUpdateGoals), .payload.validationErrors is an array of
 * { where, field, message } describing every row that failed  callers that
 * want field-level error display can read it from there.
 */
export class DataPosterError extends Error {
  /**
   * @param {string}  message   - Human-readable error message
   * @param {number}  status    - HTTP status code (0 = network failure)
   * @param {unknown} payload   - Raw error payload from the backend (may be null)
   */
  constructor(message, status, payload) {
    super(message);
    this.name = "DataPosterError";
    this.status = status;
    this.payload = payload;
  }

  /**
   * Convenience accessor for field-level validation errors, if this error
   * came from dataPost.js's 400 validation_failed response. Empty array for
   * every other error type (network, 500, unknown_route, etc.).
   *
   * @returns {Array<{ where: string, field: string, message: string }>}
   */
  get validationErrors() {
    return Array.isArray(this.payload?.validationErrors)
      ? this.payload.validationErrors
      : [];
  }
}

// ─── Goals Management - Update ────────────────────────────────────────────────

/**
 * Persist edits made in the Edit Goals modal. Upserts any combination of the
 * 3 goal tables in one transactional call on the backend.
 *
 * Mirrors dataPost.js's handleUpdateGoals 1:1 — the `payload` shape below is
 * exactly what useEditGoals.js's draft state already looks like (and exactly
 * what dataServe.js's fetchGoalsManagement() returns for weeklyRollGoals /
 * productionGoals / ytdCompletionGoals), so callers can pass the draft
 * objects straight through with no reshaping.
 *
 * All 3 top-level sections are OPTIONAL — omit a section (or pass an empty
 * `days`/array/`months`) to leave that table untouched.
 *
 * Extra fields on each row (weekStart, weekEnd, dayLabel, goalId, updatedAt,
 * hasGoal, rollGoalId, wrapGoalId, year, month, …) are harmless to include —
 * the backend reads only the fields documented below and ignores the rest,
 * so the raw draft objects can be sent as-is without reshaping.
 *
 * ── lineLeader ────────────────────────────────────────────────────────────────
 * lineLeader is a week-level field on weeklyRollGoals (not per-day).
 * This function is transparent to it at the runtime level — apimPost()
 * serialises the full payload object including weeklyRollGoals.lineLeader
 * and sends it to the backend verbatim.  dataPost.js's handleUpdateGoals
 * reads it from body.weeklyRollGoals.lineLeader and writes it to every
 * RTLS_weeklyRollsGoals row for the week.
 *
 * lineLeader does NOT appear in the response — the backend does not return a
 * new goalId or any other identifier for it.  The frontend treats the
 * draft's lineLeader value as the committed value after a successful save,
 * without needing anything from the response to confirm it.
 *
 * @param {{
 *   weeklyRollGoals?: {
 *     lineLeader?: string | null,   ← NEW: week-level roll leader name.
 *                                         null or omitted → backend writes NULL.
 *                                         Trimmed non-empty string → written to
 *                                         every row of the week in one pass.
 *     days: Array<{
 *       date: string,
 *       roll: number,
 *       wrap: number,
 *       [key: string]: unknown
 *     }>
 *   },
 *   productionGoals?: Array<{
 *     goalName:  string,
 *     goalValue: number,
 *     [key: string]: unknown
 *   }>,
 *   ytdCompletionGoals?: {
 *     months: Array<{
 *       monthDate: string,
 *       goalValue: number,
 *       [key: string]: unknown
 *     }>
 *   },
 * }} payload
 * @param {AbortSignal} [signal]
 *
 * @returns {Promise<{
 *   ok: true,
 *   updated: {
 *     weeklyRollGoals:    Array<{ date: string, goalType: "roll" | "wrap", goalId: string | null }>,
 *     productionGoals:    Array<{ goalName: string, goalId: string | null }>,
 *     ytdCompletionGoals: Array<{ monthDate: string, goalId: string | null }>,
 *   },
 * }>}
 *   `goalId` is populated for every row that was upserted, including rows
 *   that were just INSERTed (previously goalId: null on the frontend) — callers
 *   should merge these back into local state so freshly-created YTD months or
 *   roll/wrap rows stop looking "New" without requiring a full refetch.
 *   lineLeader is NOT included in the response — no merge needed.
 *
 * @throws {DataPosterError}
 *   .status === 400 with .validationErrors populated — one or more rows
 *     failed validation; NOTHING was saved (dataPost.js validates the whole
 *     payload before writing anything).
 *   .status === 500 — the transaction failed and was rolled back; NOTHING
 *     was saved.
 *   .status === 0 — network/abort failure before reaching the backend.
 */
export async function fetchUpdateGoals(payload, signal) {
  const json = await apimPost("update-goals", payload, signal);

  const rawUpdated = json?.updated ?? {};

  return {
    ok: Boolean(json?.ok),
    updated: {
      weeklyRollGoals: Array.isArray(rawUpdated.weeklyRollGoals)
        ? rawUpdated.weeklyRollGoals.map((row) => ({
            date: row.date ?? null,
            goalType: row.goalType ?? null,
            goalId: row.goalId ?? null,
          }))
        : [],
      productionGoals: Array.isArray(rawUpdated.productionGoals)
        ? rawUpdated.productionGoals.map((row) => ({
            goalName: row.goalName ?? null,
            goalId: row.goalId ?? null,
          }))
        : [],
      ytdCompletionGoals: Array.isArray(rawUpdated.ytdCompletionGoals)
        ? rawUpdated.ytdCompletionGoals.map((row) => ({
            monthDate: row.monthDate ?? null,
            goalId: row.goalId ?? null,
          }))
        : [],
    },
  };
}
